import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import { SeminarFilterOptionModel } from '../../../models/seminar.model';
import { getSeminarFirstDateString } from '../../../utils/seminar.utils';
import { SelectOption } from '../custom-select/custom-select.component';
import { Filter } from '../custom-table/custom-table.component';

@Component({
  selector: 'app-filter',
  templateUrl: './filter.component.html',
  styleUrl: './filter.component.scss',
  standalone: false,
})
export class FilterComponent<T> {
  @Input() filters: Filter[] = []; // Filter criteria to be applied
  @Input() data: T[] = []; // Input data that needs to be filtered
  @Output() filteredData = new EventEmitter<T[]>(); // Emits the filtered data
  @Output() filterChange = new EventEmitter<Filter[]>(); // Emits the filter criteria
  @Output() filterMenuOpenChange = new EventEmitter<boolean>(); // Emits the filter menu open state
  @Input() filterTitle = 'Filter'; // Title of the filter menu

  // filter-menu.component.ts
  @Input() isOpen = false;

  public getSeminarDateString = getSeminarFirstDateString;

  private initialFilters: Filter[] = [];

  // Dynamically bind the `active` class to the host element based on `isOpen`
  @HostBinding('class.active') get activeClass() {
    return this.isOpen;
  }

  ngOnInit() {
    this.filters.forEach(filter => {
      if (filter.type === 'daterange' && !filter.value) {
        filter.value = { startDate: null, endDate: null }; // Initialize value with default dates
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('ngOnChanges', changes);
    // if (changes['isOpen'] && !changes['isOpen'].firstChange) {
    //   if (!this.isOpen) {
    //     this.applyFilters();
    //   }
    // }
    // if (changes['isOpen']) {
    //   if (this.isOpen) {
    //     this.initialFilters = JSON.parse(JSON.stringify(this.filters));
    //   }
    // }
    // console.log(this.filters);
  }

  applyFilters() {
    const result = this.data?.filter(item => {
      return this.filters.every(filter => {
        const value = filter.value;

        // Handle Date Range Filter (daterange)
        if (filter.type === 'daterange') {
          if (!value?.startDate || !value?.endDate) {
            return true; // No date range selected
          }
          const filterStartDate = new Date(value.startDate);
          const filterEndDate = new Date(value.endDate);
          const itemValue = this.getNestedValue(item, filter.filteredColumn);

          if (Array.isArray(itemValue)) {
            return itemValue.some(entry => {
              if (typeof entry === 'string' || entry instanceof Date) {
                // If entry is a single date
                const itemDate = new Date(entry);
                return itemDate >= filterStartDate && itemDate <= filterEndDate;
              } else if (entry && entry.startDate && entry.endDate) {
                // If entry is an object with startDate and endDate
                const itemStartDate = new Date(entry.startDate);
                const itemEndDate = new Date(entry.endDate);
                return (
                  itemStartDate <= filterEndDate &&
                  itemEndDate >= filterStartDate // Overlap check
                );
              }
              return false; // Unsupported entry type
            });
          } else {
            // If the item value is a single date
            const itemDate = new Date(itemValue);
            return itemDate >= filterStartDate && itemDate <= filterEndDate;
          }
        }

        // Handle Single Date Filter (date)
        if (filter.type === 'date') {
          if (!value) {
            return true; // No date selected
          }
          const selectedDate = new Date(value);
          const itemDate = new Date(
            this.getNestedValue(item, filter.filteredColumn)
          );
          return itemDate.toDateString() === selectedDate.toDateString(); // Compare dates
        }

        // Handle Select Filter (select)
        if (filter.type === 'select') {
          return value
            ? this.getNestedValue(item, filter.filteredColumn) === value
            : true;
        }

        if (filter.type === 'checkbox') {
          const itemValue = this.getNestedValue(item, filter.filteredColumn);
          return value ? true : !itemValue;
        }

        // Handle Multi-select Filter (multiselect)
        if (filter.type === 'multiselect') {
          if (!value || value.length === 0) {
            return true;
          }

          const itemValue = this.getNestedValue(item, filter.filteredColumn);

          if (Array.isArray(itemValue)) {
            return itemValue.some(entry => value.includes(entry));
          }

          return value.includes(itemValue);
        }

        // Handle String Filter (string)
        if (filter.type === 'text') {
          const searchString = value?.toLowerCase();
          const itemString = (
            this.getNestedValue(item, filter.filteredColumn) || ''
          ).toLowerCase();
          return itemString.includes(searchString); // Case-insensitive string match
        }

        return true;
      });
    });

    this.filteredData.emit(result);
    this.filterChange.emit(this.filters); // Emit the updated filter criteria
    this.filterMenuOpenChange.emit(false); // Close the filter menu
    console.log('applyFilters', this.filters);
  }

  resetFilters() {
    this.filters.forEach(filter => {
      if (filter.type === 'daterange') {
        filter.value = { startDate: null, endDate: null }; // Reset date range
      } else if (filter.type === 'checkbox') {
        filter.value = false;
      } else {
        filter.value = null; // Reset other filters
      }
    });
  }

  onClose(cancel: boolean = false) {
    if (cancel) {
      this.filters = JSON.parse(JSON.stringify(this.initialFilters));
    }
    this.filterMenuOpenChange.emit(false); // Close the filter menu
  }

  getNestedValue(obj: any, path: string): any {
    const keys = path.split('.'); // Split the path into keys based on dot notation
    let result = obj;

    for (const key of keys) {
      if (result && result.hasOwnProperty(key)) {
        result = result[key];
      } else {
        return undefined; // Return undefined if the path doesn't exist
      }
    }

    return result;
  }

  getStatusChip(
    status: string,
    selectOptions?: SelectOption[]
  ): { class: string; text: string } {
    switch (status) {
      case 'online':
        return { class: 'inwi-chip-text-green-outline', text: 'Online' };
      case 'finished':
        return { class: 'inwi-chip-text-grey-outline', text: 'Vorbei' };
      case 'waitlist':
        return { class: 'inwi-chip-text-red-outline', text: 'Warteliste' };
      case 'draft':
        return { class: 'inwi-chip-text-grey-outline', text: 'Entwurf' };
      case 'canceled':
        return { class: 'inwi-chip-text-red-outline', text: 'Abgesagt' };
      case 'confirmed':
        return { class: 'inwi-chip-text-green-outline', text: 'Bezahlt' };
      case 'pending':
        return { class: 'inwi-chip-text-grey-outline', text: 'Ausstehend' };
      case 'cancelled':
        return { class: 'inwi-chip-text-red-outline', text: 'Storniert' };
      case 'late':
        return { class: 'inwi-chip-text-yellow-outline', text: 'Verspätet' };
      case 'paid':
        return { class: 'inwi-chip-text-green-outline', text: 'Bezahlt' };
      case 'unpaid':
        return { class: 'inwi-chip-text-grey-outline', text: 'Ausstehend' };
      case 'sold':
        return { class: 'inwi-chip-text-yellow-outline', text: 'Verkauft' };
      case 'deleted':
        return { class: 'inwi-chip-text-red-outline', text: 'Gelöscht' };
      case 'inactive':
        return { class: 'inwi-chip-text-red-outline', text: 'Inaktiv' };
      case 'used':
        return { class: 'inwi-chip-text-green-outline', text: 'Verwendet' };
      case 'active':
        return { class: 'inwi-chip-text-green-outline', text: 'Aktiv' };
      case 'expired':
        return { class: 'inwi-chip-text-red-outline', text: 'Abgelaufen' };
      default:
        return {
          class: 'inwi-chip-text-white-outline',
          text:
            selectOptions?.find(option => option.value === status)?.label ||
            'Unbekannt',
        };
    }
  }

  removeStatusFromFilter(event: MouseEvent, status: string, filter: Filter) {
    event.stopPropagation();
    filter.value = filter.value.filter(value => value !== status);
  }

  public stopPropagation(event: MouseEvent) {
    event.stopPropagation();
  }

  getSeminarTitle(seminarId: number) {
    const seminar = this.filters
      .find(filter => filter.type === 'seminar')
      ?.selectOptions?.find(
        (option: SeminarFilterOptionModel) => option.id === seminarId
      ) as SeminarFilterOptionModel | undefined;
    return seminar
      ? seminar.title + ' (' + this.getSeminarDateString(seminar) + ')'
      : '';
  }
}
